import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { CONTACT, WA_LINK, prefillQuote, GMB_MESSAGE } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
    "Hotel / Resort",
    "Restaurant / Café",
    "Real Estate",
    "School / College",
    "Hospital / Clinic",
    "Showroom",
    "Gym / Spa",
    "Office",
    "Industry / Factory",
    "Other",
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        business_type: "",
        plan: "",
        message: "",
    });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        const onPlan = (e) =>
            setForm((f) => ({ ...f, plan: e.detail, message: `I'm interested in the ${e.detail} plan.` }));
        const onPrefill = (e) =>
            setForm((f) => ({ ...f, message: e.detail }));
        window.addEventListener("select-plan", onPlan);
        window.addEventListener("prefill-quote", onPrefill);
        return () => {
            window.removeEventListener("select-plan", onPlan);
            window.removeEventListener("prefill-quote", onPrefill);
        };
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/leads`, {
                name: form.name,
                phone: form.phone,
                email: form.email,
                business_type: form.business_type,
                plan: form.plan || null,
                message: form.message,
            });
            toast.success("Request received!", {
                description: "Our team will reach out within a few hours.",
            });
            setForm({ name: "", phone: "", email: "", business_type: "", plan: "", message: "" });
        } catch (err) {
            toast.error("Could not send your request", {
                description: "Please try again or reach us on WhatsApp.",
            });
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full rounded-xl border border-[#00C6FF]/20 bg-[#050B14]/60 px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-[#00C6FF]/60";

    return (
        <section id="contact" className="relative py-20 lg:py-32" data-testid="contact-section">
            <div className="absolute left-1/3 top-0 h-72 w-72 rounded-full bg-[#00C6FF]/8 blur-[130px]" />
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid gap-14 lg:grid-cols-2">
                    <div>
                        <SectionHead
                            chapter="13"
                            eyebrow="Contact"
                            title="LET'S BUILD YOUR 360° EXPERIENCE"
                            sub="Tell us about your space and we'll craft a custom quote — usually within a few hours."
                        />
                        <div className="mt-10 space-y-4">
                            {[
                                { icon: Phone, label: "Call Us", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}`, id: "contact-phone" },
                                { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, id: "contact-email" },
                                { icon: MapPin, label: "Studio", value: CONTACT.location, href: null, id: "contact-location" },
                            ].map((c) => (
                                <Reveal key={c.id}>
                                    <div className="glass-card flex items-center gap-4 rounded-2xl p-5" data-testid={c.id}>
                                        <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#00C6FF]/40 text-[#00C6FF]">
                                            <c.icon size={19} />
                                        </span>
                                        <div>
                                            <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-slate-500">
                                                {c.label}
                                            </p>
                                            {c.href ? (
                                                <a href={c.href} className="font-semibold text-white hover:text-[#00C6FF]">
                                                    {c.value}
                                                </a>
                                            ) : (
                                                <p className="font-semibold text-white">{c.value}</p>
                                            )}
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                            <Reveal delay={0.1}>
                                <a
                                    data-testid="contact-whatsapp-btn"
                                    href={WA_LINK}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-3 rounded-full bg-[#25D366] px-7 py-4 text-sm font-bold uppercase tracking-[0.15em] text-[#050B14] transition-transform duration-300 hover:scale-[1.02]"
                                >
                                    <MessageCircle size={18} />
                                    Chat on WhatsApp
                                </a>
                            </Reveal>
                            <Reveal delay={0.18}>
                                <div className="glass-card rounded-2xl p-5" data-testid="contact-gmb-card">
                                    <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#00C6FF]">
                                        Invisible on Google?
                                    </p>
                                    <p className="mt-2 text-sm text-slate-300">
                                        No listing means no 'near me' customers. We'll create your
                                        Google Business Profile from scratch and put your 360° tour
                                        right inside it.
                                    </p>
                                    <button
                                        data-testid="contact-gmb-btn"
                                        onClick={() => prefillQuote(GMB_MESSAGE)}
                                        className="mt-4 rounded-full border border-[#00C6FF]/50 px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-[#00C6FF] transition-colors hover:bg-[#00C6FF]/10"
                                    >
                                        Create My Google Listing
                                    </button>
                                </div>
                            </Reveal>
                        </div>
                    </div>

                    <Reveal delay={0.15}>
                        <form
                            onSubmit={submit}
                            data-testid="lead-form"
                            className="glass-card rounded-2xl p-8"
                        >
                            <h3 className="text-xl font-bold text-white">Get Custom Quote</h3>
                            <p className="mt-1 text-sm text-slate-400">
                                360° experiences starting from ₹4,999
                            </p>
                            <div className="mt-7 space-y-4">
                                <input
                                    data-testid="lead-name-input"
                                    required
                                    minLength={2}
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder="Your name"
                                    className={inputCls}
                                />
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <input
                                        data-testid="lead-phone-input"
                                        required
                                        minLength={7}
                                        value={form.phone}
                                        onChange={set("phone")}
                                        placeholder="Phone number"
                                        className={inputCls}
                                    />
                                    <input
                                        data-testid="lead-email-input"
                                        required
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="Email address"
                                        className={inputCls}
                                    />
                                </div>
                                <select
                                    data-testid="lead-business-type-select"
                                    value={form.business_type}
                                    onChange={set("business_type")}
                                    className={`${inputCls} appearance-none`}
                                >
                                    <option value="" disabled>
                                        Type of space
                                    </option>
                                    {BUSINESS_TYPES.map((t) => (
                                        <option key={t} value={t} className="bg-[#0A1F44]">
                                            {t}
                                        </option>
                                    ))}
                                </select>
                                <textarea
                                    data-testid="lead-message-input"
                                    rows={4}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder="Tell us about your space (size, locations, goals...)"
                                    className={`${inputCls} resize-none`}
                                />
                                <button
                                    data-testid="lead-submit-btn"
                                    type="submit"
                                    disabled={sending}
                                    className="cta-glow flex w-full items-center justify-center gap-2 rounded-full bg-[#00C6FF] py-4 text-sm font-bold uppercase tracking-[0.15em] text-[#050B14] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
                                >
                                    <Send size={16} />
                                    {sending ? "Sending..." : "Request My Quote"}
                                </button>
                            </div>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
